/**
 * API методы для аналитики рассылок
 * Дата: 30.01.2026 - Создан для TODO-046 (Аналитика рассылок)
 * Связан с: internal/handler/analytics.go, frontend/src/pages/Analytics.tsx
 */
import { apiClient } from './client';

// Типы для аналитики рассылок
export interface BroadcastDTO {
  id: string;
  phone: string;
  patient_name?: string;
  template_id: string;
  template_name?: string;
  template_slug?: string;
  messenger: string;
  status: string;
  sent_at: string;
  delivered_at?: string;
  appointment_datetime?: string;
}

export interface BroadcastFilters {
  tenant_id?: string;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  template_id?: string;
  status?: string; // all, delivered, failed, pending
  messenger?: string; // all, whatsapp_waba, telegram, max
  limit?: number;
  offset?: number;
}

export interface BroadcastSummary {
  total: number;
  delivered: number;
  failed: number;
  pending: number;
}

export interface BroadcastsListResponse {
  broadcasts: BroadcastDTO[];
  summary: BroadcastSummary;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Типы для запланированных сообщений
// Дата: 01.02.2026 - Добавлено для разделения Запланировано/Отправлено
export interface ScheduledMessageDTO {
  id: string;
  campaign_id: string;
  phone: string;
  patient_name?: string;
  content: string;
  scheduled_at: string;
  status: string; // pending, sent, failed, skipped
  attempts: number;
  result?: string;
}

export interface ScheduledFilters {
  tenant_id?: string;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  status?: string; // all, pending, sent, failed, skipped
  search?: string; // поиск по телефону или имени
  limit?: number;
  offset?: number;
}

export interface ScheduledSummary {
  total: number;
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
}

export interface ScheduledListResponse {
  messages: ScheduledMessageDTO[];
  summary: ScheduledSummary;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Типы для напоминаний по записям
// Дата: 01.02.2026 - Добавлено для вкладки "По записям"
export interface AppointmentReminderDTO {
  id: string;
  phone: string;
  patient_name: string;
  doctor_name: string;
  appointment_datetime: string;
  status: string;
  reminder_24h_sent: boolean;
  reminder_2h_sent: boolean;
  feedback_sent: boolean;
  service_name?: string;
}

export interface AppointmentReminderFilters {
  tenant_id?: string;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  status?: string; // all, scheduled, completed, cancelled, no_show
  reminder?: string; // all, 24h, 2h, feedback, pending
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AppointmentReminderSummary {
  total_appointments: number;
  reminder_24h_sent: number;
  reminder_24h_total: number;
  reminder_2h_sent: number;
  reminder_2h_total: number;
  feedback_sent: number;
  feedback_total: number;
}

export interface AppointmentRemindersResponse {
  appointments: AppointmentReminderDTO[];
  summary: AppointmentReminderSummary;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const analyticsApi = {
  // Список отправленных рассылок с фильтрацией
  getBroadcasts: async (filters: BroadcastFilters): Promise<BroadcastsListResponse> => {
    const params: Record<string, string> = {};
    if (filters.tenant_id) params.tenant_id = filters.tenant_id;
    params.from_date = filters.from_date;
    params.to_date = filters.to_date;
    if (filters.template_id) params.template_id = filters.template_id;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.messenger && filters.messenger !== 'all') params.messenger = filters.messenger;
    if (filters.limit !== undefined) params.limit = String(filters.limit);
    if (filters.offset !== undefined) params.offset = String(filters.offset);

    const response = await apiClient.get<BroadcastsListResponse>('/analytics/broadcasts', { params });
    return response.data;
  },

  // Список запланированных сообщений с фильтрацией
  // Дата: 01.02.2026 - Добавлено для разделения Запланировано/Отправлено
  getScheduled: async (filters: ScheduledFilters): Promise<ScheduledListResponse> => {
    const params: Record<string, string> = {};
    if (filters.tenant_id) params.tenant_id = filters.tenant_id;
    params.from_date = filters.from_date;
    params.to_date = filters.to_date;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.search) params.search = filters.search;
    if (filters.limit !== undefined) params.limit = String(filters.limit);
    if (filters.offset !== undefined) params.offset = String(filters.offset);

    const response = await apiClient.get<ScheduledListResponse>('/analytics/scheduled', { params });
    return response.data;
  },

  // История напоминаний по записям
  // Дата: 01.02.2026 - Добавлено для вкладки "По записям"
  getAppointmentReminders: async (filters: AppointmentReminderFilters): Promise<AppointmentRemindersResponse> => {
    const params: Record<string, string> = {};
    if (filters.tenant_id) params.tenant_id = filters.tenant_id;
    params.from_date = filters.from_date;
    params.to_date = filters.to_date;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.reminder && filters.reminder !== 'all') params.reminder = filters.reminder;
    if (filters.search) params.search = filters.search;
    if (filters.limit !== undefined) params.limit = String(filters.limit);
    if (filters.offset !== undefined) params.offset = String(filters.offset);

    const response = await apiClient.get<AppointmentRemindersResponse>('/analytics/appointment-reminders', { params });
    return response.data;
  },
};
